const peerConnections = new Map();
let localStream;
let screenStream;
const remoteStreams = new Map();
const pendingIceCandidates = new Map();

const ICE_SERVERS = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
  ],
};

// 🎥 Get camera + mic
export const getLocalStream = async () => {
  if (!navigator?.mediaDevices?.getUserMedia) {
    throw new Error("Media devices not available");
  }

  const constraintsList = [
    { video: { width: { ideal: 1280 }, height: { ideal: 720 } }, audio: true },
    { video: true, audio: true },
    { video: false, audio: true },
  ];

  let lastError;
  for (const constraints of constraintsList) {
    try {
      localStream = await navigator.mediaDevices.getUserMedia(constraints);
      return localStream;
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Unable to access media devices");
};

// 🖥️ Get screen share stream
export const getScreenStream = async () => {
  try {
    screenStream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        cursor: "always",
      },
      audio: false,
    });
    return screenStream;
  } catch (error) {
    console.error("Error getting screen share:", error);
    return null;
  }
};

// 🔄 Replace video track (for screen share)
export const replaceVideoTrack = async (newStream) => {
  const videoTrack = newStream.getVideoTracks()[0];
  if (!videoTrack) return;

  await Promise.all(
    Array.from(peerConnections.values()).map(async (connection) => {
      const sender = connection.getSenders().find(s => s.track?.kind === 'video');
      if (sender) {
        await sender.replaceTrack(videoTrack);
      }
    })
  );
  console.log('🔄 Video track replaced');
};

// 🔗 Create Peer Connection
export const createPeerConnection = (socket, remoteSocketId) => {
  if (typeof RTCPeerConnection === "undefined") {
    console.error("WebRTC not supported in this environment.");
    return null;
  }

  const peerConnection = new RTCPeerConnection(ICE_SERVERS);
  const remoteStream = new MediaStream();
  peerConnections.set(remoteSocketId, peerConnection);
  remoteStreams.set(remoteSocketId, remoteStream);

  // Remote stream
  peerConnection.ontrack = (event) => {
    event.streams[0].getTracks().forEach(track => {
      remoteStream.addTrack(track);
    });
  };

  // ICE candidates
  peerConnection.onicecandidate = (event) => {
    if (event.candidate) {
      socket.emit("ice-candidate", {
        to: remoteSocketId,
        candidate: event.candidate,
      });
    }
  };

  // Add local tracks
  if (!localStream) {
    console.warn("⚠️ Local stream not ready yet");
  } else {
    localStream.getTracks().forEach(track => {
      peerConnection.addTrack(track, localStream);
    });
  }

  return remoteStream;
};

// 📞 Caller creates offer
export const createOffer = async (remoteSocketId) => {
  const peerConnection = peerConnections.get(remoteSocketId);
  if (!peerConnection) return null;
  const offer = await peerConnection.createOffer();
  await peerConnection.setLocalDescription(offer);
  return offer;
};

// 📥 Receiver handles offer
export const handleOffer = async (offer, remoteSocketId) => {
  const peerConnection = peerConnections.get(remoteSocketId);
  if (!peerConnection) return null;
  await peerConnection.setRemoteDescription(offer);
  const queued = pendingIceCandidates.get(remoteSocketId) || [];
  for (const candidate of queued) {
    await peerConnection.addIceCandidate(candidate);
  }
  pendingIceCandidates.delete(remoteSocketId);
  const answer = await peerConnection.createAnswer();
  await peerConnection.setLocalDescription(answer);
  return answer;
};

// 📥 Caller receives answer
export const setAnswer = async (answer, remoteSocketId) => {
  const peerConnection = peerConnections.get(remoteSocketId);
  if (!peerConnection) return;
  await peerConnection.setRemoteDescription(answer);
  const queued = pendingIceCandidates.get(remoteSocketId) || [];
  for (const candidate of queued) {
    await peerConnection.addIceCandidate(candidate);
  }
  pendingIceCandidates.delete(remoteSocketId);
};

// ❄️ Add ICE candidate
export const addIceCandidate = async (candidate, remoteSocketId) => {
  const peerConnection = peerConnections.get(remoteSocketId);
  if (!peerConnection) return;
  if (candidate) {
    if (!peerConnection.remoteDescription) {
      const queued = pendingIceCandidates.get(remoteSocketId) || [];
      queued.push(candidate);
      pendingIceCandidates.set(remoteSocketId, queued);
      return;
    }
    await peerConnection.addIceCandidate(candidate);
  }
};

// ❌ End call - stops all tracks and closes connection
export const closeConnection = (remoteSocketId) => {
  if (remoteSocketId) {
    const peerConnection = peerConnections.get(remoteSocketId);
    if (peerConnection) {
      peerConnection.close();
      peerConnections.delete(remoteSocketId);
    }
    const remoteStream = remoteStreams.get(remoteSocketId);
    if (remoteStream) {
      remoteStream.getTracks().forEach(track => track.stop());
      remoteStreams.delete(remoteSocketId);
    }
    pendingIceCandidates.delete(remoteSocketId);
    return;
  }

  // Stop all local stream tracks (camera + mic)
  if (localStream) {
    localStream.getTracks().forEach(track => {
      track.stop();
      console.log('🛑 Stopped track:', track.kind);
    });
    localStream = null;
  }
  
  // Stop screen share tracks
  if (screenStream) {
    screenStream.getTracks().forEach(track => track.stop());
    screenStream = null;
  }
  
  // Stop remote stream tracks
  remoteStreams.forEach((stream) => {
    stream.getTracks().forEach(track => track.stop());
  });
  remoteStreams.clear();
  pendingIceCandidates.clear();
  
  // Close peer connections
  peerConnections.forEach((connection) => connection.close());
  peerConnections.clear();
  
  console.log('📴 Connection closed, all tracks stopped');
};

// 🖥️ Stop screen share and switch back to camera
export const stopScreenShare = async () => {
  if (screenStream) {
    screenStream.getTracks().forEach(track => track.stop());
    screenStream = null;
  }
  
  // Switch back to camera
  if (localStream) {
    const videoTrack = localStream.getVideoTracks()[0];
    if (!videoTrack) return;

    await Promise.all(
      Array.from(peerConnections.values()).map(async (connection) => {
        const sender = connection.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          await sender.replaceTrack(videoTrack);
        }
      })
    );
  }
};

export const setLocalStream = (stream) => {
  localStream = stream;
};

export const getRemoteStream = (remoteSocketId) => remoteStreams.get(remoteSocketId);
